import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './api-error.js';
import { sortQuerySchema } from './sort.js';

/**
 * Normalized event names. Providers report their own vocabulary; ingest maps every
 * provider onto this list so checks and the mail log never branch on provider.
 *
 * @since 1.0.0
 */
export const MAIL_EVENT_TYPES = [
    'accepted',
    'delivered',
    'temporary_fail',
    'permanent_fail',
    'rejected',
    'complained',
    'unsubscribed',
] as const;

export const mailEventTypeSchema = z.enum(MAIL_EVENT_TYPES);

export type MailEventType = z.infer<typeof mailEventTypeSchema>;

/**
 * Event types that mean a lead did not arrive. Used by the FAILURES filter chip and
 * by the bounce checks.
 *
 * @since 1.0.0
 */
export const FAILURE_EVENT_TYPES: readonly MailEventType[] = ['temporary_fail', 'permanent_fail', 'rejected'];

/**
 * Distinguishes real client leads from our own canary traffic. Derived at ingest
 * from the recipient, so it works without provider-side tagging.
 *
 * @since 1.0.0
 */
export const MAIL_TAGS = ['lead', 'fkwd-qc'] as const;

export const mailTagSchema = z.enum(MAIL_TAGS);

export type MailTag = z.infer<typeof mailTagSchema>;

export const mailEventSchema = z.object({
    id: z.number().int(),
    providerEventId: z.string(),
    clientId: z.number().int().nullable(),
    clientSlug: z.string().nullable(),
    clientName: z.string().nullable(),
    domain: z.string(),
    eventType: mailEventTypeSchema,
    eventAt: z.number().int(),
    recipient: z.string(),
    subject: z.string().nullable(),
    messageId: z.string().nullable(),
    qcTag: mailTagSchema,
    severity: z.string().nullable(),
    reason: z.string().nullable(),
    deliveryCode: z.number().int().nullable(),
    deliveryMessage: z.string().nullable(),
    qcRunId: z.string().nullable(),
});

export type MailEvent = z.infer<typeof mailEventSchema>;

/**
 * One page of the mail log.
 *
 * @since 1.3.0
 */
export const mailEventsPageSchema = paginatedSchema(mailEventSchema);

export type MailEventsPage = z.infer<typeof mailEventsPageSchema>;

/**
 * Filter chips on the mail log, in display order.
 *
 * @since 1.0.0
 */
export const MAIL_FILTERS = ['all', 'failures', 'delivered', 'qc'] as const;

export const mailFilterSchema = z.enum(MAIL_FILTERS);

export type MailFilter = z.infer<typeof mailFilterSchema>;

/**
 * Columns the mail log may be sorted on, named after the table's own column keys.
 *
 * @since 1.4.0
 */
export const MAIL_EVENT_SORT_KEYS = ['time', 'client', 'recipient', 'subject', 'event', 'tag'] as const;

export const mailEventSortKeySchema = z.enum(MAIL_EVENT_SORT_KEYS);

export type MailEventSortKey = z.infer<typeof mailEventSortKeySchema>;

export const mailEventQuerySchema = paginationQuerySchema
    .extend(sortQuerySchema(MAIL_EVENT_SORT_KEYS, 'time', 'desc').shape)
    .extend({
        filter: mailFilterSchema.default('all'),
        client: z.string().optional(),
        q: z.string().max(200).optional(),
        from: z.coerce.number().int().optional(),
        to: z.coerce.number().int().optional(),
    });

export type MailEventQuery = z.infer<typeof mailEventQuerySchema>;

/**
 * One hour of a client's mail volume. Twenty-four of these draw the lead-flow
 * sparkline without touching the event log.
 *
 * @since 1.0.0
 */
export const mailStatsHourSchema = z.object({
    hourAt: z.number().int(),
    delivered: z.number().int(),
    failed: z.number().int(),
    temporaryFailed: z.number().int(),
    qcDelivered: z.number().int(),
});

export type MailStatsHour = z.infer<typeof mailStatsHourSchema>;
